import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import styles from "./ReorderChip.module.css";
import { CustomButton, CustomIconButton, CustomTooltip, icon } from "../../../../../../../common/components";
import {
	resetReorderError,
	setReorderIndexDirection,
	setReorderIndexDirectionByOrder,
} from "../../../../../slice/pdf/pdf-slice";
import { isNonEmptyArray } from "../../../../../../../common/utils";
import { translate } from "../../../../../../../common/providers";

type ReorderChipProps = {
	rectangle: { order: number; pageStart: number };
	onEditingChange?: (isEditing: boolean) => void;
};

type RedrContent = {
	pageStart: number;
	contentId: number;
	content: string;
	contentType: string;
	order: number;
};
type ReqContent = {
	informationId: number;
	rectangles: { order: number; pageStart: number }[];
};

type PageContent = {
	page: number;
	redr: RedrContent[];
	req: ReqContent[];
};

const selectPageContents = createSelector(
	[(state) => state.srPdf.content, (_, pageStart) => pageStart],
	(content: { page: number; redr: RedrContent[]; req: ReqContent[] }[], pageStart) => {
		if (!pageStart) {
			return [];
		}
		const pages = new Set([pageStart - 1, pageStart, pageStart + 1]);
		return content.filter((p) => pages.has(p.page)).map(({ page, redr, req }) => ({ page, redr, req }));
	}
);

const selectOrdersInScope = createSelector(
	[(state) => state.srPdf.content, (_, pageStart) => pageStart],
	(content: { page: number; redr: RedrContent[]; req: ReqContent[] }[], pageStart) => {
		if (!pageStart) {
			return [];
		}
		const pages = new Set([pageStart - 1, pageStart, pageStart + 1]);
		return content
			.filter((p) => pages.has(p.page))
			.flatMap((p) => (p.redr ?? []).map((r) => r.order))
			.sort((a, b) => a - b);
	}
);

const ReorderChip: React.FC<ReorderChipProps> = ({ rectangle, onEditingChange }) => {
	const reorderRedressmentFocusActive = useSelector(({ srPdf }) => srPdf.reorderRedressmentFocusActive);
	const reorderContentOrder: number | null = useSelector(({ srPdf }) => srPdf.reorderContentOrder);
	const reorderContentDirectionAbove = useSelector(({ srPdf }) => srPdf.reorderContentDirectionAbove);
	const pageContents = useSelector((state) => selectPageContents(state, reorderRedressmentFocusActive));
	const ordersInScope = useSelector((state) => selectOrdersInScope(state, rectangle.pageStart));
	const reorderTargetOrder = useSelector(({ srPdf }) => srPdf.reorderContentTargetOrder);
	const reorderDirectByOrderError = useSelector(({ srPdf }) => srPdf.reorderDirectByOrderError);
	const [isOriginalEditing, setIsOriginalEditing] = useState(false);
	const isEditing = isOriginalEditing;
	const [inputValue, setInputValue] = useState("");
	const [inputError, setInputError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const dispatch = useDispatch();
	const setIsEditing = useCallback(
		(value: boolean) => {
			setIsOriginalEditing(value);
			onEditingChange?.(value);
		},
		[onEditingChange]
	);

	useEffect(() => {
		if (isEditing) {
			inputRef.current?.focus();
			inputRef.current?.select();
		}
	}, [isEditing]);
	useEffect(() => {
		if (reorderContentOrder !== null && isEditing) {
			setIsEditing(false);
		}
	}, [reorderContentOrder, isEditing, setIsEditing]);
	const handleClick = (next: boolean) => {
		dispatch(setReorderIndexDirection({ rectangle, above: next }));
	};
	const handleOrderClick = () => {
		if (!reorderContentOrder) {
			setInputValue(String(rectangle.order));
			setInputError(null);
			setIsEditing(true);
		}
	};
	useEffect(() => {
		if (reorderDirectByOrderError) {
			setInputError(translate("smart-review.redress-reorder.error"));
		}
	}, [reorderDirectByOrderError]);
	const handleInputConfirm = () => {
		const parsed = Number(inputValue);
		if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
			setInputError(translate("smart-review.redress-reorder.error.nan"));
			return;
		}
		if (parsed === rectangle.order) {
			setIsEditing(false);
			return;
		}
		if (!ordersInScope.includes(parsed)) {
			setInputError(translate("smart-review.redress-reorder.error.out-of-scope"));
			return;
		}
		setInputError(null);
		dispatch(setReorderIndexDirectionByOrder({ rectangle, targetOrder: parsed }));
	};
	const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
			e.preventDefault();
			e.stopPropagation();
		}
		if (e.key === "Enter") {
			handleInputConfirm();
		}
		if (e.key === "Escape") {
			setIsEditing(false);
			setInputError(null);
			dispatch(resetReorderError(null));
		}
	};
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
		setInputError(null);
		if (reorderDirectByOrderError) {
			dispatch(resetReorderError(null));
		}
	};
	const { isFirst, isLast, isOrdering, orderText, isPreviousPossible, isNextPossible } = useMemo(() => {
		if (!isNonEmptyArray(pageContents)) {
			return {
				isFirst: false,
				isLast: false,
				isOrdering: false,
				orderText: rectangle.order,
				isPreviousPossible: false,
				isNextPossible: false,
			};
		}
		const toRectanglesWithMeta = (page: PageContent) =>
			page.req?.flatMap((info) =>
				info.rectangles.map((r) => ({ ...r, page: page.page, infoId: info.informationId }))
			) ?? [];
		const allRectangles = [...pageContents]
			.sort((a, b) => a.page - b.page)
			.flatMap(toRectanglesWithMeta)
			.sort((a, b) => a.order - b.order);
		const currentIndex = allRectangles.findIndex((r) => r.order === rectangle.order);
		if (currentIndex === -1) {
			return {
				isFirst: false,
				isLast: false,
				isOrdering: false,
				orderText: rectangle.order,
				isPreviousPossible: false,
				isNextPossible: false,
			};
		}
		const currentRect = allRectangles[currentIndex];
		const tempIsFirst = currentIndex === 0;
		const tempIsLast = currentIndex === allRectangles.length - 1;
		const reorderIndex =
			reorderContentOrder === null ? -1 : allRectangles.findIndex((r) => r.order === reorderContentOrder);
		let tempIsOrdering = false;
		let tempOrderText = rectangle.order;
		if (reorderIndex !== -1) {
			const swapIndex = (() => {
				if (reorderTargetOrder !== null) {
					return allRectangles.findIndex((r) => r.order === reorderTargetOrder);
				}
				return reorderContentDirectionAbove ? reorderIndex + 1 : reorderIndex - 1;
			})();

			tempIsOrdering = currentIndex === reorderIndex || currentIndex === swapIndex;
			if (currentIndex === reorderIndex) {
				tempOrderText = allRectangles[swapIndex]?.order ?? rectangle.order;
			} else if (currentIndex === swapIndex) {
				tempOrderText = allRectangles[reorderIndex]?.order ?? rectangle.order;
			}
		}
		const currentInfoRects =
			pageContents
				.flatMap((page) => page.req ?? [])
				.find((info) => info.rectangles.some((r) => r.order === rectangle.order))?.rectangles ?? [];
		const sortedRects = [...currentInfoRects].sort((a, b) => a.order - b.order);
		const rectIndex = sortedRects.findIndex((r) => r.order === rectangle.order);
		const previousInformation = currentIndex > 0 ? allRectangles[currentIndex - 1] : undefined;
		const nextInformation = currentIndex < allRectangles.length - 1 ? allRectangles[currentIndex + 1] : undefined;
		const countRectsForInfo = (infoId: number) =>
			pageContents.flatMap((page) => page.req ?? []).find((info) => info.informationId === infoId)?.rectangles
				.length ?? 0;
		const tempIsPreviousPossible =
			(sortedRects.length > 1 && rectIndex > 0) ||
			(sortedRects.length === 1 &&
				previousInformation !== undefined &&
				previousInformation.infoId !== currentRect.infoId &&
				countRectsForInfo(previousInformation.infoId) === 1);
		const tempIsNextPossible =
			(sortedRects.length > 1 && rectIndex < sortedRects.length - 1) ||
			(sortedRects.length === 1 &&
				nextInformation !== undefined &&
				nextInformation.infoId !== currentRect.infoId &&
				countRectsForInfo(nextInformation.infoId) === 1);
		return {
			isFirst: tempIsFirst,
			isLast: tempIsLast,
			isOrdering: tempIsOrdering,
			orderText: tempOrderText,
			isPreviousPossible: tempIsPreviousPossible,
			isNextPossible: tempIsNextPossible,
		};
	}, [rectangle, pageContents, reorderContentOrder, reorderContentDirectionAbove, reorderTargetOrder]);
	const renderOrderBadge = () => (
		<CustomTooltip open={!!inputError} title={inputError}>
			<span
				className={styles.inputWrapper}
				onBlur={(e) => {
					if (!e.currentTarget.contains(e.relatedTarget)) {
						handleInputConfirm();
					}
				}}
			>
				<CustomIconButton
					className={styles.stepButton}
					icon={icon.faChevronDown}
					size="xs"
					tabIndex={-1}
					onClick={() => {
						const currentIdx = ordersInScope.indexOf(Number(inputValue));
						const nextIdx = currentIdx === -1 ? 0 : currentIdx - 1;
						if (nextIdx >= 0) setInputValue(String(ordersInScope[nextIdx]));
					}}
					onMouseDown={(e) => e.preventDefault()}
				/>
				<input
					ref={inputRef}
					className={styles.orderInput}
					type="number"
					value={inputValue}
					onChange={handleInputChange}
					onKeyDown={handleInputKeyDown}
				/>
				<CustomIconButton
					className={styles.stepButton}
					icon={icon.faChevronUp}
					size="xs"
					tabIndex={-1}
					onClick={() => {
						const currentIdx = ordersInScope.indexOf(Number(inputValue));
						const nextIdx = currentIdx === -1 ? ordersInScope.length - 1 : currentIdx + 1;
						if (nextIdx < ordersInScope.length) setInputValue(String(ordersInScope[nextIdx]));
					}}
					onMouseDown={(e) => e.preventDefault()}
				/>
			</span>
		</CustomTooltip>
	);
	return (
		<div
			className={styles.chip}
			data-is-editing={isEditing}
			data-is-first={isFirst || !isPreviousPossible}
			data-is-last={isLast || !isNextPossible}
			data-is-order-ongoing={reorderContentOrder !== null}
			data-is-ordering={isOrdering}
		>
			{!isFirst && !reorderContentOrder && isPreviousPossible && !isEditing && (
				<CustomIconButton
					className={styles.icon}
					color="var(--grey-500)"
					icon={icon.faChevronLeft}
					size="xs"
					onClick={() => handleClick(false)}
				/>
			)}
			{(isEditing && renderOrderBadge()) || (
				<CustomButton
					className={`${styles.orderText} ${isOrdering ? styles.orderTextBold : ""}`}
					tabIndex={0}
					onClick={handleOrderClick}
					onKeyDown={(e) => e.key === "Enter" && handleOrderClick()}
				>
					{orderText}
				</CustomButton>
			)}
			{!isLast && !reorderContentOrder && isNextPossible && !isEditing && (
				<CustomIconButton
					className={styles.icon}
					color="var(--grey-500)"
					icon={icon.faChevronRight}
					size="xs"
					onClick={() => handleClick(true)}
				/>
			)}
		</div>
	);
};

export default ReorderChip;
